<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";

interface Props {
  mode?: "determinate" | "indeterminate"
  value?: number
  height?: string
  /** CSS color of the fill, a var from toaster.css by default. */
  fill?: string
  /** Accessible name of the bar, such as the title of its toast. */
  label?: string
}

const props = withDefaults(defineProps<Props>(), {
  mode: "determinate",
  value: 0,
  height: "6px",
  fill: "var(--vb-fill)",
  label: undefined,
});

const currentWidth = ref(0);
const isIndeterminate = computed(() => props.mode === "indeterminate" || props.value === 0);

// Declare animationFrame outside the watch to maintain reference
let animationFrame: number | undefined;

// Smooth progress animation
watch(() => props.value, (newValue) => {
  if (isIndeterminate.value) {
    currentWidth.value = 0;
    return;
  }

  // Cancel any existing animation
  if (animationFrame !== undefined) {
    cancelAnimationFrame(animationFrame);
  }

  const startWidth = currentWidth.value;
  const targetWidth = newValue;
  const startTime = performance.now();
  const duration = 400; // animation duration in ms

  function animate(currentTime: number) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // Easing function for smoother animation using ** operator instead of Math.pow
    const eased = progress === 1 ? 1 : 1 - 2 ** (-10 * progress);

    currentWidth.value = startWidth + (targetWidth - startWidth) * eased;

    if (progress < 1) {
      animationFrame = requestAnimationFrame(animate);
    }
  }

  animationFrame = requestAnimationFrame(animate);
}, { immediate: true });

// Clean up animation frame on component unmount
onUnmounted(() => {
  if (animationFrame !== undefined) {
    cancelAnimationFrame(animationFrame);
  }
});
</script>

<template>
  <div
    class="progress-track"
    role="progressbar"
    :aria-label="label"
    aria-valuemin="0"
    aria-valuemax="100"
    :aria-valuenow="isIndeterminate ? undefined : Math.round(value)"
    :style="{ height }"
  >
    <div
      class="progress-fill"
      :class="{ 'animate-progress': isIndeterminate }"
      :style="{ background: fill, ...(isIndeterminate ? {} : { width: `${currentWidth}%` }) }"
    />
  </div>
</template>

<style scoped>
.progress-track {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: var(--vb-track);
  border-radius: 999px;
}

.progress-fill {
  height: 100%;
  border-radius: 999px;
}

@keyframes progress {
  0% {
    width: 0%;
    margin-left: -20%;
  }
  100% {
    width: 40%;
    margin-left: 100%;
  }
}

.animate-progress {
  animation: progress 1.5s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .animate-progress {
    animation: none;
    width: 40%;
  }
}
</style>
